import { FolderFileEntry, Document } from '@/types/document.types'

// Utilidades genéricas de archivos viven ahora en `src/utils/fileUtils.ts`.
// Se re-exportan aquí para no romper imports existentes del módulo de documents.
export {
  getFileExtension,
  removeExtension,
  formatFileName,
  formatFileSize,
} from '@/utils/fileUtils'

import { formatFileName, getFileExtension } from '@/utils/fileUtils'

/**
 * Obtiene el tipo de archivo legible en español
 * @param mimeType - MIME type del archivo
 * @returns Nombre legible del tipo (e.g., "PDF", "Word", "Imagen")
 */
export const getReadableFileType = (mimeType: string): string => {
  const typeMap: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'image/jpeg': 'Imagen',
    'image/png': 'Imagen',
  };
  return typeMap[mimeType] || 'Documento';
};

/**
 * Extrae FolderFileEntry[] de un input con webkitdirectory
 */
export const extractFilesFromInput = (files: FileList): FolderFileEntry[] => {
  const entries: FolderFileEntry[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    entries.push({
      file,
      relativePath: file.webkitRelativePath || file.name,
    });
  }
  return entries;
};

/**
 * Extrae FolderFileEntry[] de un DataTransferItemList (drag-and-drop)
 * Usa FileSystem API para recorrer recursivamente la carpeta
 */
export const extractFilesFromDrop = async (
  items: DataTransferItemList
): Promise<FolderFileEntry[]> => {
  const entries: FolderFileEntry[] = [];

  const readEntry = (entry: FileSystemEntry, path: string): Promise<void> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file((file: File) => {
          entries.push({ file, relativePath: path + file.name });
          resolve();
        });
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        reader.readEntries(async (subEntries: FileSystemEntry[]) => {
          for (const subEntry of subEntries) {
            await readEntry(subEntry, path + entry.name + '/');
          }
          resolve();
        });
      } else {
        resolve();
      }
    });
  };

  const rootEntries: FileSystemEntry[] = [];
  for (let i = 0; i < items.length; i++) {
    const entry = items[i].webkitGetAsEntry?.();
    if (entry) rootEntries.push(entry);
  }

  for (const entry of rootEntries) {
    await readEntry(entry, '');
  }

  return entries;
};

/**
 * Formatea una ruta relativa de carpeta para almacenamiento,
 * aplicando formatFileName a cada segmento del path
 */
export const formatFolderPath = (relativePath: string): string => {
  const parts = relativePath.split('/');
  return parts
    .map((part, index) => {
      if (index === parts.length - 1) {
        const ext = getFileExtension(part);
        const nameWithoutExt = part.replace(/\.[^/.]+$/, '');
        return formatFileName(nameWithoutExt) + ext.toLowerCase();
      }
      return formatFileName(part);
    })
    .join('/');
};

/**
 * Obtiene el nombre de la carpeta raiz de una lista de FolderFileEntry
 */
export const getRootFolderName = (entries: FolderFileEntry[]): string => {
  if (entries.length === 0) return '';
  const firstPath = entries[0].relativePath;
  const rootFolder = firstPath.split('/')[0];
  return rootFolder || '';
};

/**
 * Grupo de documentos que pertenecen a una misma carpeta
 */
export interface FolderGroup {
  folderKey: string;
  folderName: string;
  tag: string;
  fileCount: number;
  documents: Document[];
}

/**
 * Agrupa documentos por carpeta raiz basandose en la estructura del URL.
 * URLs con 3+ segmentos (tag/folder/file) se agrupan bajo la carpeta.
 * URLs con 2 segmentos (tag/file) son archivos sueltos.
 */
export const groupDocumentsByFolder = (documents: Document[]): {
  folders: FolderGroup[];
  standaloneFiles: Document[];
} => {
  const folderMap = new Map<string, { tag: string; folderName: string; docs: Document[] }>();
  const standaloneFiles: Document[] = [];

  for (const doc of documents) {
    const segments = doc.url.split('/');
    if (segments.length >= 3) {
      const folderKey = `${segments[0]}/${segments[1]}`;
      if (!folderMap.has(folderKey)) {
        folderMap.set(folderKey, {
          tag: segments[0],
          folderName: segments[1],
          docs: [],
        });
      }
      folderMap.get(folderKey)!.docs.push(doc);
    } else {
      standaloneFiles.push(doc);
    }
  }

  const folders: FolderGroup[] = Array.from(folderMap.entries()).map(([key, value]) => ({
    folderKey: key,
    folderName: value.folderName,
    tag: value.tag,
    fileCount: value.docs.length,
    documents: value.docs,
  }));

  return { folders, standaloneFiles };
};
